import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Badge,
  Box,
  ClickAwayListener,
  IconButton,
  Chip,
  Popover,
  Stack,
  Typography,
  Modal,
  Button
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import CustomToolbar from 'src/components/common/BigCalendar/CustomToolbar';
import { useCalendarRange } from 'src/components/hooks/useCalendarRange';
import './BigCalendar.scss';
import { CalendarView } from './CalendarView';
import ScheduleAppBar from './Components/ScheduleAppBar';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { CreateEvent, EditEvent } from 'src/types/Events';
import { getCalendarEvents, getEditEvent, getNewEvent } from 'src/helpers/events';
import ScheduleEventDialog from './Components/ScheduleEventDialog';
import { useGetCodeListDataQuery } from 'src/services/codeList';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { useClenderEvents } from './useClenderEvents';
import { setMessage } from 'src/store/Message';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { hideLoader, showLoader } from 'src/store/Loader';
import { archLevel } from 'src/data/config';
import { AgendaView } from './Agenda';
import { useDataReview } from '../../DataMonitoring/useDataReview';
import { getSelectedSubjectVisitCalenderEvents, getSubjectVisitsCalendarEvents } from 'src/helpers/subjectCalender';
import { StudyLegendsInfo, SubjectVisit } from 'src/types/SubjectCalenderVisits';
import ShowSubjectVisitDialog from './ShowSubjectVisitDialog';
import UnscheduleVisitDialog from './Components/UnscheduleVisitDialog';
import { useUtil } from 'src/components/hooks/useUtil';
import { useLazyTsdaCalendarViewQuery } from 'src/services/trailNotebookCRF';

const shapeStyles = { width: 20, height: 20 };
const circle = <Box component="span" sx={{ ...shapeStyles }} />;

const VisitSchedulePage: React.FunctionComponent<any> = (props: any) => {
  const { checkPermission } = usePermissionWrapper();
  const hasCalendarAccess =
    (archLevel == 'sponsor' && checkPermission('view_monitoring_visits_sponsor')) ||
    (archLevel == 'site' && checkPermission('view_monitoring_visits_site'));
  const hasSubjectsVisitsAccess =
    (archLevel == 'sponsor' && checkPermission('view_subject_visits_calendar_sponsor')) ||
    (archLevel == 'site' && checkPermission('view_subject_visits_calendar_site'));
  const { userPreferredDateTimeFormat } = usePreferedDateFormat();
  const dispatch = useAppDispatch();
  const canCreateCalEvent = checkPermission('create_cal_event');
  const canUpdateCalEvent = checkPermission('update_cal_event');
  const { data: visitTypesList } = useGetCodeListDataQuery('VISITTYPE');
  const { data: subVisitTypesList } = useGetCodeListDataQuery('SUBTYPEVISIT');
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const currentHierarchyLevel = useAppSelector(state => state.site.currentHierarchyLevel);
  const [subjectsList, setSubjectsList] = useState<string[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<any>([]);
  const [studyVisitsLegendsInfo, setStudyVisitsLegendsInfo] = useState<StudyLegendsInfo[]>([]);
  const localizer = momentLocalizer(moment);
  const [refreshKey, setRefreshKey] = useState(0);

  const [calendarView, setCalendarView] = useState<string | null>(CalendarView.ALL_SUBJECTS);
  const { startOn, onRangeChange } = useCalendarRange(
    moment().startOf('week').toDate(),
    moment().endOf('week').toDate()
  );
  const [view, setView] = useState<View>('month');
  const [openCustomPopup, setOpenCustomPopup] = useState(false);
  const [popupEvents, setPopupEvents] = useState<any[]>([]);
  const [popupDate, setPopupDate] = useState<Date>(new Date());
  const getDrilldownView = (_: any, currentView: any) => (currentView === 'month' ? 'month' : null);

  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [eventCreateUpdate, setEventCreateUpdate] = useState<CreateEvent | EditEvent>(getNewEvent());
  const [eventDialogIsOpen, setEventDialogIsOpen] = useState(false);
  const [visitDialogIsOpen, setVisitDialogIsOpen] = useState(false);
  const [tempMonitoringEvents, setTempMonitoringEvents] = useState<any>([]);
  const [tempSubjectVisits, setTempSubjectVisits] = useState<any>([]);
  const [tempSubjectVisitsEvents, setTempSubjectVisitsEvents] = useState<any>([]);
  const [selectedSubject, setSelectedSubject] = useState<any>({});
  const { data: durationList } = useGetCodeListDataQuery('VISTDURTN');
  const unscheduledVisitPermission = checkPermission('create_unscheduled_subject_visit');
  const [openUnschedulePopup, setOpenUnschedulePopup] = useState<boolean>(false);
  const { handleError } = useUtil();
  const {
    getMonitoringEvent,
    monitoringEventIsError,
    monitoringEventError,
    getUnscheduledVisitCrfs,
    unscheduledVisitCrfsData,
    unscheduledVisitCrfsIsError,
    unscheduledVisitCrfsError
  } = useClenderEvents();
  const { getSubjectsCalendarVisitsData } = useDataReview();
  const [getSubjectsCalendarVisitsDataTsda] = useLazyTsdaCalendarViewQuery();
  const [visitCrfs, setVisitCrfs] = useState<any[]>([]);

  const getMonitoringEventData = async () => {
    dispatch(showLoader());
    const monitoringResponse: any = await getMonitoringEvent({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: archLevel === 'sponsor' ? process.env.REACT_APP_NAME : process.env.REACT_APP_NAME_TEMP
    });
    if (monitoringResponse.isSuccess) {
      const monitoringVisitData = getCalendarEvents(monitoringResponse.data);
      setTempMonitoringEvents([...monitoringVisitData]);
    } else if (monitoringResponse.isError) {
      setTempMonitoringEvents([]);
      if (monitoringResponse.error?.status === 500) {
        dispatch(
          setMessage({
            msgDetails: monitoringResponse.error?.message,
            severity: 'error'
          })
        );
      }
    }
    dispatch(hideLoader());
  };
  const getAllSubjectsCalenderVisits = async () => {
    dispatch(showLoader());
    const subjectCalVisitResponse: any =
      archLevel === 'sponsor'
        ? await getSubjectsCalendarVisitsData({
            site_id: selectedSite,
            study_id: studyId,
            country: selectedCountry,
            application: process.env.REACT_APP_NAME
          })
        : await getSubjectsCalendarVisitsDataTsda({
            site_id: selectedSite,
            study_id: studyId,
            country: selectedCountry,
            application: process.env.REACT_APP_NAME_TEMP
          });
    if (subjectCalVisitResponse?.isSuccess) {
      const subjectVisitData = getSubjectVisitsCalendarEvents(subjectCalVisitResponse.data);
      setSubjectsList(subjectVisitData.subjectsList);
      setStudyVisitsLegendsInfo([...subjectVisitData.visitsLegendsInfo]);
      setTempSubjectVisits(subjectCalVisitResponse.data);
      setTempSubjectVisitsEvents([...subjectVisitData.allCalendarVisits]);
    } else if (subjectCalVisitResponse?.isError) {
      setSubjectsList([]);
      setStudyVisitsLegendsInfo([]);
      setTempSubjectVisits([]);
      setTempSubjectVisitsEvents([]);
      if (subjectCalVisitResponse.error?.status === 500) {
        dispatch(
          setMessage({
            msgDetails: subjectCalVisitResponse.error?.message,
            severity: 'error'
          })
        );
      }
    }
    dispatch(hideLoader());
  };
  const resetSelectedCalenderView = () => {
    setSelectedSubject({});
    setCalendarView(CalendarView.ALL_SUBJECTS);
  };
  const resetSubjectVisitsDataInfo = () => {
    setCalendarEvents([]);
    setSubjectsList([]);
    setStudyVisitsLegendsInfo([]);
    setTempSubjectVisits([]);
    resetSelectedCalenderView();
    setRefreshKey(res => res + 1);
  };
  useEffect(() => {
    setCalendarEvents([...tempMonitoringEvents, ...tempSubjectVisitsEvents]);
  }, [tempMonitoringEvents, tempSubjectVisitsEvents]);
  useEffect(() => {
    resetSubjectVisitsDataInfo();
  }, []);
  const getAllCalendarData = async () => {
    if (archLevel === 'sponsor') {
      if (studyId) {
        if (hasCalendarAccess) {
          await getMonitoringEventData();
        }
        if (hasSubjectsVisitsAccess) {
          resetSelectedCalenderView();
          await getAllSubjectsCalenderVisits();
        }
      } else {
        resetSubjectVisitsDataInfo();
        if (archLevel === 'sponsor' && hasSubjectsVisitsAccess) {
          dispatch(
            setMessage({
              msgDetails: 'Study must be selected to show the subjects and its visits',
              severity: 'error'
            })
          );
        }
      }
    } else if (archLevel === 'site') {
      if (selectedSite) {
        if (hasCalendarAccess) {
          await getMonitoringEventData();
        }
        if (hasSubjectsVisitsAccess) {
          resetSelectedCalenderView();
          await getAllSubjectsCalenderVisits();
        }
      } else {
        resetSubjectVisitsDataInfo();
      }
    }
  };
  useEffect(() => {
    getAllCalendarData();
    if (archLevel == 'site' && selectedSite) {
      getUnscheduledVisitCrfs({
        study_id: studyId,
        country: selectedCountry,
        site_id: selectedSite,
        application: process.env.REACT_APP_NAME_TEMP
      });
    }
  }, [studyId, selectedCountry, selectedSite, currentHierarchyLevel]);

  useEffect(() => {
    if (unscheduledVisitCrfsIsError) {
      const err: any = unscheduledVisitCrfsError;
      handleError(err);
      setVisitCrfs([]);
    }
  }, [unscheduledVisitCrfsIsError]);

  useEffect(() => {
    if (unscheduledVisitCrfsData) {
      setVisitCrfs(unscheduledVisitCrfsData?.visit_crf);
    }
  }, [unscheduledVisitCrfsData]);

  useEffect(() => {
    if (monitoringEventIsError) {
      const err: any = monitoringEventError;
      if (err?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403: Restricted User permission',
            severity: 'error'
          })
        );
      } else if (err?.status == 404) {
        return;
      }
    }
  }, [monitoringEventIsError]);

  const handleNewEvent = (isEvent: boolean) => {
    if (isEvent) {
      if (archLevel != 'site') {
        setEventCreateUpdate(getNewEvent());
        setEventDialogIsOpen(true);
      } else {
        setOpenUnschedulePopup(true);
      }
    }
  };
  const getSelectedSubjectData = (subjectId: string | null) => {
    if (tempSubjectVisits.subject_visit_details.length > 0) {
      const tempSelectedSubject = tempSubjectVisits.subject_visit_details.find(
        (subjectVisitDetails: any) => subjectVisitDetails.subject.subject_id === subjectId
      );
      setSelectedSubject(tempSelectedSubject);
    }
  };
  const handleSelectedEvent = (selectedEvent: any) => {
    setSelectedEvent(selectedEvent);
    getSelectedSubjectData(selectedEvent.subject_id);
    setOpenEditPopover(true);
  };

  const onCalendarViewChange = (newView: string | null) => {
    let tempSubjectCalEvents: any = [];
    if (newView === CalendarView.ALL_SUBJECTS) {
      tempSubjectCalEvents = getSubjectVisitsCalendarEvents(tempSubjectVisits);
      setSelectedSubject({});
    } else {
      tempSubjectCalEvents = getSelectedSubjectVisitCalenderEvents(newView, tempSubjectVisits);
      getSelectedSubjectData(newView);
    }
    setCalendarEvents([...tempMonitoringEvents, ...tempSubjectCalEvents.allCalendarVisits]);
    setCalendarView(newView);
    setSelectedEvent(null);
  };
  const handleCustomPopup = (events: any[], date: Date) => {
    setPopupEvents(events);
    setPopupDate(date);
    setOpenCustomPopup(true);
  };

  const handlePopupEventClick = (event: any, selectedEvent: any) => {
    handleCustomPopoverClose(event);
    getSelectedSubjectData(selectedEvent.subject_id);
    setSelectedEvent(selectedEvent);
    setOpenEditPopover(true);
  };
  const handleCustomPopoverClose = (event: any) => {
    setOpenCustomPopup(false);
  };

  const getPopupContentViewerValue = (singleEvent: any) => {
    if (
      singleEvent.visit_type === 'Interim Monitoring Visit' ||
      singleEvent.visit_type === 'Site Qualification Visit' ||
      singleEvent.visit_type === 'Site Initiation Visit'
    ) {
      return singleEvent.title;
    } else {
      return singleEvent.title;
    }
  };

  // To open Schedule Assetment Popover
  const [openEditPopover, setOpenEditPopover] = useState(false);
  const handleCloseEditPopover = () => {
    setSelectedSubject({});
    setOpenEditPopover(false);
  };

  const handleEditEvent = () => {
    setEventCreateUpdate(getEditEvent(selectedEvent));
    setEventDialogIsOpen(true);
    handleCloseEditPopover();
  };
  const handleOpenSubjectVisitInfo = () => {
    setVisitDialogIsOpen(true);
    handleCloseEditPopover();
  };

  const getDateCellEligibleForColor = (
    currentDay: Date,
    startOn: string,
    minRange: number,
    maxRange: number
  ): boolean => {
    if (minRange === 0 || maxRange === 0) {
      if (minRange === 0) {
        return maxRange > 0 ? moment(currentDay).isSameOrBefore(moment(`${startOn}Z`).add(maxRange, 'days')) : true;
      } else if (maxRange === 0) {
        return minRange > 0 ? moment(currentDay).isSameOrAfter(moment(`${startOn}Z`).add(minRange, 'days')) : true;
      }
    } else {
      return moment(currentDay).isBetween(
        moment(`${startOn}Z`).subtract(minRange + 1, 'days'),
        moment(`${startOn}Z`).add(maxRange, 'days')
      );
    }
    return false;
  };

  const customDateCellWrapper = (props: any) => {
    let cellUnderVisits = 0;
    if (selectedSubject && Object.keys(selectedSubject).length > 0 && selectedSubject.visits.length > 0) {
      selectedSubject.visits.forEach((visit: SubjectVisit) => {
        if (
          getDateCellEligibleForColor(props.value, visit.start_on, visit.minimum_visit_range, visit.maximum_visit_range)
        ) {
          cellUnderVisits++;
        }
      });
    }
    return (
      <div
        style={{ position: 'relative', height: '100%', flex: '1 1', zIndex: 1 }}
        className={props.children.props.className}
      >
        {!props.children.props.className.split(' ').includes('rbc-off-range-bg') &&
        !props.children.props.className.split(' ').includes('rbc-today') ? (
          <div
            style={{
              position: 'absolute',
              inset: 0
            }}
          >
            {view === 'day' && (
              <span
                style={{
                  position: 'absolute',
                  inset: 0
                }}
              >
                {moment(props.value).format('D dddd')}
              </span>
            )}
            {selectedSubject && Object.keys(selectedSubject).length > 0 ? (
              <>
                {selectedSubject.visits.length > 0 &&
                  selectedSubject.visits.map((eachVisit: SubjectVisit, index: number) => {
                    return getDateCellEligibleForColor(
                      props.value,
                      eachVisit.start_on,
                      eachVisit.minimum_visit_range,
                      eachVisit.maximum_visit_range
                    ) ? (
                      <div
                        key={`${eachVisit.protocol_visit_name}-${index + 1}`}
                        style={{
                          backgroundColor: eachVisit.visit_color ? eachVisit.visit_color : `#ffffff`,
                          height: `${100 / cellUnderVisits}%`,
                          width: '100%'
                        }}
                      >
                        &#160;
                      </div>
                    ) : (
                      <></>
                    );
                  })}
              </>
            ) : null}
          </div>
        ) : (
          view === 'day' && (
            <div
              style={{
                position: 'absolute',
                inset: 0
              }}
            >
              {moment(props.value).format('D dddd')}
            </div>
          )
        )}
      </div>
    );
  };
  const getEventPropGetterStyle = (event: any) => {
    if (
      selectedSubject &&
      Object.keys(selectedSubject).length > 0 &&
      selectedEvent?.subject_id &&
      event.subject_id === selectedEvent?.subject_id &&
      event.protocol_visit_name === selectedEvent?.protocol_visit_name
    ) {
      return { className: 'selectedSubject' };
    } else {
      return event.visit_type === 'Interim Monitoring Visit' ? { className: `monitoringEvent` } : {};
    }
  };

  return (
    <>
      <Box className="site-contact-page" sx={{ padding: '0 16px 0 225px', minHeight: '100%' }}>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ScheduleAppBar
              hasCalendarAccess={hasCalendarAccess}
              hasSubjectCalanderVisitAccess={hasSubjectsVisitsAccess}
              calendarView={calendarView}
              viewsList={[...Object.values(CalendarView), ...subjectsList]}
              canCreateCalEvent={canCreateCalEvent}
              onViewChange={newView => newView && onCalendarViewChange(newView)}
              onCreate={handleNewEvent}
              unscheduledVisitPermission={unscheduledVisitPermission}
            />
          </Box>
          <Box sx={{ pt: 1, mt: 0 }}>
            <Calendar
              key={`bigcalendar-${refreshKey}`}
              defaultDate={new Date()}
              views={{
                week: true,
                month: true,
                day: true,
                agenda: AgendaView // PICTMS_2656 - Custom Agenda View for the Calendar
              }}
              showMultiDayTimes
              toolbar
              style={{ minHeight: 'calc(100vh - 175px)', width: '100%', zIndex: 4 }}
              localizer={localizer}
              events={calendarEvents}
              scrollToTime={moment(startOn).set('hour', 8).toDate()}
              startAccessor="start"
              endAccessor="end"
              formats={{
                dateFormat: 'D',
                dayFormat: 'D dddd',
                weekdayFormat: 'dddd'
              }}
              onRangeChange={onRangeChange}
              onView={setView}
              onSelectEvent={handleSelectedEvent}
              eventPropGetter={(event: any) => getEventPropGetterStyle(event)}
              popup={false}
              components={{
                toolbar: CustomToolbar,
                dateCellWrapper: customDateCellWrapper
              }}
              onShowMore={(events: any[], date: Date) => {
                handleCustomPopup(events, date);
              }}
              getDrilldownView={getDrilldownView}
            />
            {openCustomPopup && (
              <Modal open={openCustomPopup} onClose={handleCustomPopoverClose}>
                <Box
                  sx={{
                    position: 'absolute',
                    width: '200px',
                    zIndex: '10',
                    top: '30%',
                    left: '55%',
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <ClickAwayListener onClickAway={handleCustomPopoverClose}>
                    <div className="rbc-overlay">
                      <div className="rbc-overlay-header">
                        {moment(popupDate).format('MMMM D')}
                        <IconButton
                          sx={{ float: 'right', mr: -1, marginTop: '-6px' }}
                          onClick={handleCustomPopoverClose}
                        >
                          <CloseIcon />
                        </IconButton>
                      </div>
                      <div className="calOverflow">
                        {popupEvents.map((singleEvent: any, index: number) => {
                          return (
                            <div
                              className={`rbc-event rbc-event-continues-after${
                                singleEvent.visit_type === 'Interim Monitoring Visit'
                                  ? ' monitoringEvent'
                                  : ' otherVisits'
                              }`}
                              key={`${singleEvent.visit_type}-${index + 1}`}
                            >
                              <div
                                className="rbc-event-content"
                                title={getPopupContentViewerValue(singleEvent)}
                                onClick={e => handlePopupEventClick(e, singleEvent)}
                              >
                                {getPopupContentViewerValue(singleEvent)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </ClickAwayListener>
                </Box>
              </Modal>
            )}
            {openEditPopover && (
              <Popover
                open={openEditPopover}
                anchorEl={null}
                onClose={handleCloseEditPopover}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
              >
                <Box sx={{ py: 2, px: 2.5, width: '420px' }}>
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ width: '100%' }}>
                      {selectedEvent?.subject_id ? (
                        <Button onClick={handleOpenSubjectVisitInfo} sx={{ paddingLeft: 0 }}>
                          <Typography variant="h5" component="div" color="primary" sx={{ fontWeight: 500, mb: 0.5 }}>
                            {selectedEvent?.subject_id}
                          </Typography>
                        </Button>
                      ) : (
                        <Typography variant="h5" component="div" color="primary" sx={{ fontWeight: 500, mb: 0.5 }}>
                          Site Visit{' '}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        width: '106px',
                        mr: '-8px !important',
                        mb: '0px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-start'
                      }}
                    >
                      {!selectedEvent?.subject_id && (
                        <IconButton
                          onClick={handleEditEvent}
                          disabled={
                            !canUpdateCalEvent || currentHierarchyLevel !== 'site' || selectedEvent?.is_checkin !== null
                          } /**
                      selectedEvent?.is_checkin !== null --- This condition is added because edit button will be enabled only for the first time when the visit is created before checkin. Once the checkin is done the is_Checkin flag is changed to true and if the user change the start or end date then is_checkin is changed to false but button should not get enabled. It should only get enabled when is_checkin flag is null
                       */
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton onClick={handleCloseEditPopover}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Stack>
                  <Stack direction="row">
                    <Box sx={{ mb: '8px' }}>
                      {selectedEvent?.subject_id && (
                        <Typography variant="body2" gutterBottom>
                          {`${moment(`${selectedEvent?.start_on}Z`).format('LT')} -
                        ${moment(`${selectedEvent?.start_on}Z`)
                          .add(selectedEvent?.duration, 'minutes')
                          .format('LT')}`}{' '}
                          ({selectedEvent?.duration / 60} hours)
                          <br />
                          <br />
                          <dl className="dlStyles">
                            <dt>Visit Name:</dt>{' '}
                            <dd>
                              {selectedEvent?.unscheduled_visit_name
                                ? selectedEvent?.unscheduled_visit_name
                                : selectedEvent?.protocol_visit_name}
                            </dd>
                          </dl>
                        </Typography>
                      )}
                      {!selectedEvent?.subject_id && (
                        <Typography variant="body2" gutterBottom>
                          {`${moment(selectedEvent?.start_date).format(userPreferredDateTimeFormat)} -
                        ${moment(selectedEvent?.end_date).format(userPreferredDateTimeFormat)}`}{' '}
                          ({moment(selectedEvent?.end_date).diff(moment(selectedEvent?.start_date), 'minutes')} minutes)
                        </Typography>
                      )}
                      {!selectedEvent?.unscheduled_visit_name && (
                        <dl className="dlStyles">
                          <dt>Visit Type:</dt> <dd>{selectedEvent?.visit_type}</dd>
                        </dl>
                      )}
                      {selectedEvent?.subject_id && (
                        <>
                          <dl className="dlStyles">
                            <dt>Assessment(s):</dt> <dd>{selectedEvent?.assessments}</dd>
                          </dl>
                        </>
                      )}
                    </Box>
                  </Stack>
                  {!selectedEvent?.subject_id && (
                    <>
                      <dl className="dlStyles">
                        <dt>Study ID:</dt>{' '}
                        <dd>
                          <strong>{selectedEvent?.study_value}</strong>
                        </dd>
                      </dl>
                      <dl className="dlStyles">
                        <dt>Country:</dt>{' '}
                        <dd>
                          <strong>{selectedEvent?.country_value}</strong>
                        </dd>
                      </dl>
                      <dl className="dlStyles">
                        <dt>Site ID:</dt>{' '}
                        <dd>
                          <strong>{selectedEvent?.site_value}</strong>
                        </dd>
                      </dl>
                    </>
                  )}
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={'center'}
                    sx={{ mt: 1.5, fontWeight: 700, '& .MuiChip-label': { fontSize: '13px' } }}
                  >
                    <Box columnGap={1} display={'flex'}>
                      <Chip label="Scheduled" size="small" />
                      {selectedEvent?.created_by && <Chip label={selectedEvent?.created_by} size="small" />}
                    </Box>
                  </Stack>
                </Box>
              </Popover>
            )}
          </Box>
        </Box>
        {view !== 'agenda' && (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              lineHeight: '20px',
              mb: '4px',
              overflowX: 'auto',
              paddingBottom: '6px',
              '& > span': { display: 'inline-block', minWidth: 'fit-content', fontSize: '0.9rem' }
            }}
          >
            <span>
              <Badge className="badgeMonitoring">{circle}</Badge>Interim Monitoring Visit
            </span>
            {studyVisitsLegendsInfo.length > 0 ? (
              studyVisitsLegendsInfo.map((visitLegend: StudyLegendsInfo) => (
                <span key={visitLegend.visit_id}>
                  <Badge
                    className="circle"
                    sx={{ bgcolor: visitLegend.visit_color ? visitLegend.visit_color : '#ffffff' }}
                  >
                    {circle}
                  </Badge>
                  {visitLegend.visit_name}
                </span>
              ))
            ) : (
              <></>
            )}
          </Stack>
        )}
      </Box>
      {eventDialogIsOpen && (
        <ScheduleEventDialog
          isDialogOpen={eventDialogIsOpen}
          onClose={() => setEventDialogIsOpen(false)}
          event={eventCreateUpdate}
          calendarEvents={calendarEvents}
          visitTypesList={visitTypesList}
          subVisitTypesList={subVisitTypesList}
          onSuccess={() => {
            resetSubjectVisitsDataInfo();
            getAllCalendarData();
          }}
        />
      )}
      {visitDialogIsOpen && (
        <ShowSubjectVisitDialog
          isDialogOpen={visitDialogIsOpen}
          onClose={() => setVisitDialogIsOpen(false)}
          selectedSubjectVisit={selectedEvent}
        />
      )}
      {openUnschedulePopup && (
        <UnscheduleVisitDialog
          openUnschedulePopup={openUnschedulePopup}
          setOpenUnschedulePopup={setOpenUnschedulePopup}
          durationList={durationList}
          subjectsList={subjectsList}
          visitCrfs={visitCrfs}
          setVisitCrfs={setVisitCrfs}
          tempSubjectVisitsEvents={tempSubjectVisitsEvents}
          onSuccess={() => {
            resetSubjectVisitsDataInfo();
            getAllCalendarData();
          }}
        />
      )}
    </>
  );
};
export default VisitSchedulePage;
